import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Persistent storage and flags
if (typeof window !== 'undefined') {
    window.__scrollPositions = window.__scrollPositions || {};
    window.__prevPath = window.__prevPath || null;
    window.__scrollLocked = window.__scrollLocked || false;
    
    // CRITICAL: Lock scroll saves BEFORE any navigation
    if (!window.__scrollHandlersAdded) {
        window.__scrollHandlersAdded = true;
        
        // Capture scroll on mousedown/touchstart (before click fires)
        const saveAndLock = (e) => {
            const link = e.target.closest('a[href]');
            if (!link) return;
            
            const href = link.getAttribute('href');
            if (!href || !href.startsWith('/') || href.startsWith('//')) return;
            
            // Save current scroll and lock further saves
            const currentPath = window.location.pathname + window.location.search;
            window.__scrollPositions[currentPath] = window.scrollY;
            window.__scrollLocked = true;
            
            // Unlock after navigation completes
            setTimeout(() => { window.__scrollLocked = false; }, 2000);
        };
        
        document.addEventListener('mousedown', saveAndLock, { capture: true, passive: true });
        document.addEventListener('touchstart', saveAndLock, { capture: true, passive: true });
    }
}

/**
 * Scroll restoration component
 */
const ScrollRestoration = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const isInitialized = useRef(false);

    const getPathKey = useCallback(() => {
        return location.pathname + (location.search || '');
    }, [location.pathname, location.search]);

    // Disable browser's scroll restoration
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    // Continuous scroll saving (when not locked)
    useEffect(() => {
        const pathKey = getPathKey();
        let rafId;
        
        const saveScroll = () => {
            if (window.__scrollLocked) return;
            const browserPath = window.location.pathname + window.location.search;
            if (browserPath === pathKey) {
                window.__scrollPositions[pathKey] = window.scrollY;
            }
        };
        
        const handleScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(saveScroll);
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [getPathKey]);

    // Handle navigation
    useLayoutEffect(() => {
        const currentPath = getPathKey();
        
        if (!isInitialized.current) {
            isInitialized.current = true;
            window.__prevPath = currentPath;
            return;
        }

        if (window.__prevPath !== currentPath) {
            window.__scrollLocked = true;
            
            // Don't restore if logo refresh was triggered
            if (navigationType === 'POP' && !window.__logoRefreshTriggered) {
                const savedY = window.__scrollPositions[currentPath];
                
                if (savedY !== undefined && savedY > 0) {
                    const restore = () => window.scrollTo(0, savedY);
                    restore();
                    queueMicrotask(restore);
                    requestAnimationFrame(restore);
                    setTimeout(restore, 0);
                    setTimeout(restore, 50);
                    setTimeout(restore, 100);
                    setTimeout(restore, 200);
                    setTimeout(restore, 400);
                    setTimeout(restore, 700);
                }
            } else {
                if (!location.hash) {
                    window.scrollTo(0, 0);
                }
            }

            window.__prevPath = currentPath;
            setTimeout(() => { window.__scrollLocked = false; }, 1000);
        }
    }, [getPathKey, location.hash, navigationType]);

    return null;
};

export default ScrollRestoration;
